/**
 * One row of the inventory Table mode (Phase C2). Memoised like InventoryProductRow: its props are
 * primitives, the product object (stable across refetches by structural sharing), the visible
 * columns (one array for the whole table) and the stable `actions` — so moving the active row,
 * ticking a box or typing in the search box re-renders only the rows whose own props changed.
 *
 * Every money figure is the server's (`listedPrice`, `storePrices`), formatted, never converted.
 */

import { memo, useEffect, useRef, type KeyboardEvent } from 'react'
import { PRICE_UNAVAILABLE_LABEL, type Currency } from '../../../contexts/CurrencyContext'
import { normalizeDisplayCardName } from './inventoryDisplayUtils'
import { formatStoreAmount, isSaleActiveNow } from './pricing'
import { inventoryCopy } from './inventoryCopy'
import { formatSoldDate, unitsSoldFor } from './inventorySalesView'
import type { InventoryTableColumn } from './inventoryTableColumns'
import type { CellStatus, EditableField } from './inventoryTableEdit'
import type { InventoryProduct } from './inventoryTypes'
import styles from './InventoryTable.module.css'

export type InventoryTableRowActions = {
  activate: (index: number) => void
  open: (product: InventoryProduct) => void
  toggleSelect: (productId: string) => void
  startEdit: (index: number, field: EditableField) => void
  commitEdit: (product: InventoryProduct, field: EditableField, text: string) => void
  cancelEdit: () => void
  openImage: (url: string, alt: string) => void
}

export type InventoryTableRowProps = {
  product: InventoryProduct
  index: number
  columns: readonly InventoryTableColumn[]
  gridTemplate: string
  active: boolean
  selected: boolean
  highlighted: boolean
  canBulkOps: boolean
  canEdit: boolean
  /** The cell being edited on THIS row, if any. */
  editing: EditableField | null
  /** Starting text for the edit box. */
  editInitial: string
  status: CellStatus | undefined
  locale: string
  formatPrice: (cents: number, fromCurrency?: Currency) => string
  /** The Recent sales window, when the list has one. */
  soldWindowDays: number | null
  actions: InventoryTableRowActions
}

export const rowDomId = (productId: string) => `inventory-row-${productId}`

const money = (formatPrice: InventoryTableRowProps['formatPrice'], cents: number | null | undefined, currency: string | null | undefined) =>
  typeof cents === 'number' ? formatStoreAmount(formatPrice, cents, currency, PRICE_UNAVAILABLE_LABEL) : '—'

function EditBox({ label, initial, numeric, onCommit, onCancel }: {
  label: string
  initial: string
  numeric: 'decimal' | 'numeric'
  onCommit: (text: string) => void
  onCancel: () => void
}) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    ref.current?.focus()
    ref.current?.select()
  }, [])
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // The grid's own keys stop here.
    event.stopPropagation()
    if (event.key === 'Enter') {
      event.preventDefault()
      onCommit(event.currentTarget.value)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      onCancel()
    }
  }
  return (
    <input
      ref={ref}
      className={styles.editInput}
      defaultValue={initial}
      inputMode={numeric}
      aria-label={label}
      onKeyDown={onKeyDown}
      // Leaving the box abandons the edit: only Enter saves (a price must never save by accident).
      onBlur={onCancel}
      onClick={(event) => event.stopPropagation()}
    />
  )
}

export const InventoryTableRow = memo(function InventoryTableRow({
  product,
  index,
  columns,
  gridTemplate,
  active,
  selected,
  highlighted,
  canBulkOps,
  canEdit,
  editing,
  editInitial,
  status,
  locale,
  formatPrice,
  soldWindowDays,
  actions,
}: InventoryTableRowProps) {
  const name = normalizeDisplayCardName(product.card?.name || product.name || 'Product', product.card?.setNumber)
  const listed = product.listedPrice ?? null
  const figures = product.listedPrice ? (product.storePrices ?? null) : null
  const variant = product.variantData
  const image = product.card?.imageUrl?.[0] ?? product.imageUrls?.[0] ?? null

  const editableCell = (field: EditableField, display: string, label: string) => {
    const own = status?.field === field ? status : undefined
    if (editing === field) {
      // Text that can't be sent ("abc", below the held units) keeps the box open, with why.
      return (
        <span className={styles.editable}>
          <EditBox
            label={label}
            initial={editInitial}
            numeric={field === 'price' ? 'decimal' : 'numeric'}
            onCommit={(text) => actions.commitEdit(product, field, text)}
            onCancel={actions.cancelEdit}
          />
          {own?.state === 'error' ? <span className={styles.cellMessage} role="alert">{own.message}</span> : null}
        </span>
      )
    }
    return (
      <span className={`${styles.editable} ${own?.state === 'saving' ? styles.saving : ''} ${own?.state === 'error' ? styles.cellError : ''}`}>
        {canEdit ? (
          <button
            type="button"
            className={styles.cellButton}
            tabIndex={-1}
            onClick={(event) => { event.stopPropagation(); actions.startEdit(index, field) }}
            title={field === 'price' ? 'Edit price (e)' : 'Edit stock (q)'}
          >
            {own?.state === 'saving' ? own.text : display}
          </button>
        ) : display}
        {own?.state === 'error' ? <span className={styles.cellMessage} role="alert">{own.message}</span> : null}
      </span>
    )
  }

  const cell = (column: InventoryTableColumn) => {
    switch (column.id) {
      case 'image':
        return image ? (
          <button type="button" tabIndex={-1} className={styles.thumbButton} onClick={(event) => { event.stopPropagation(); actions.openImage(image, name) }} aria-label={`View image of ${name}`}>
            <img src={image} alt="" loading="lazy" decoding="async" className={styles.thumb} />
          </button>
        ) : <span className={styles.thumbEmpty} aria-hidden />
      case 'name':
        return (
          <button type="button" tabIndex={-1} className={styles.nameButton} onClick={(event) => { event.stopPropagation(); actions.open(product) }} title={`${name} · ${product.sku}`}>
            <span className={styles.nameText}>{name}</span>
            <span className={styles.skuText}>{product.sku}</span>
          </button>
        )
      case 'setNumber': {
        const set = product.card?.set?.setCode || product.card?.set?.setName || (typeof variant?.collection === 'string' ? variant.collection : '')
        const number = product.card?.setNumber
        return <span className={styles.truncate} title={product.card?.set?.setName}>{[set, number].filter(Boolean).join(' · ') || '—'}</span>
      }
      case 'game':
        return <span className={styles.truncate}>{product.card?.set?.game?.name || (typeof variant?.brand === 'string' ? variant.brand : '') || '—'}</span>
      case 'condition':
        return <span className={styles.truncate}>{variant?.condition || '—'}</span>
      case 'printing':
        return <span className={styles.truncate}>{variant?.printing || '—'}</span>
      case 'quantity':
        return editableCell(
          'quantity',
          product.reservedQuantity > 0 ? `${product.quantity} (${product.reservedQuantity} held)` : String(product.quantity),
          `New stock for ${name}`,
        )
      case 'listed':
        return editableCell('price', money(formatPrice, listed?.priceCents, listed?.currency), `New price for ${name}`)
      case 'manual':
        return figures?.manualPrice
          ? <span title={figures.manualPrice.exact ? 'As typed' : 'Converted at the listing rate'}>{money(formatPrice, figures.manualPrice.cents, figures.currency)}{figures.manualPrice.exact ? '' : '*'}</span>
          : '—'
      case 'sale': {
        if (!isSaleActiveNow(product)) return '—'
        if (product.saleType === 'percentage') return `${product.saleValue}% off`
        return figures?.fixedSaleOffCents != null ? `${money(formatPrice, figures.fixedSaleOffCents, figures.currency)} off` : 'Amount off'
      }
      case 'market':
        return money(formatPrice, figures?.marketPriceCents, figures?.currency)
      case 'cost':
        return figures?.costBasis ? money(formatPrice, figures.costBasis.cents, figures.currency) : '—'
      case 'margin': {
        // Listed minus cost: both the server's, same currency and rate — a subtraction, no conversion.
        if (!listed || !figures?.costBasis || listed.currency !== figures.currency) return '—'
        const margin = listed.priceCents - figures.costBasis.cents
        const pct = listed.priceCents > 0 ? Math.round((margin / listed.priceCents) * 100) : null
        return <span className={margin < 0 ? styles.negative : undefined}>{money(formatPrice, margin, listed.currency)}{pct !== null ? ` (${pct}%)` : ''}</span>
      }
      case 'tags': {
        const tags = product.manualTags ?? []
        return <span className={styles.truncate} title={tags.join(', ')}>{tags.length ? tags.join(', ') : '—'}</span>
      }
      case 'channels':
        if (product.listingStatus === 'draft') return <span className={styles.badgeMuted}>Draft</span>
        return product.salesChannels?.publicationIds?.length
          ? `${product.salesChannels.publicationIds.length} channel${product.salesChannels.publicationIds.length === 1 ? '' : 's'}`
          : 'Default'
      case 'sync':
        if (product.pushHold) return <span className={styles.badgeMuted} title={product.pushHold.reason ?? 'Held from pushing until its listing is released'}>Held</span>
        return product.needsSync ? <span className={styles.badgePending} title="Waiting to be pushed to your channels">Queued</span> : <span className={styles.badgeOk}>OK</span>
      case 'updated': {
        const at = new Date(product.updatedAt)
        return Number.isNaN(at.getTime()) ? '—' : at.toLocaleDateString(locale, { day: 'numeric', month: 'short' })
      }
      case 'lastSold':
        return product.sales ? formatSoldDate(product.sales.lastSoldAt, locale) : <span title={inventoryCopy.sales.neverSold}>—</span>
      case 'unitsSold':
        return product.sales ? unitsSoldFor(product.sales, soldWindowDays) : '—'
      case 'lastSalePrice':
        return money(formatPrice, product.sales?.lastSale.unitCents, product.sales?.lastSale.currency)
    }
  }

  return (
    <div
      id={rowDomId(product.id)}
      role="row"
      aria-rowindex={index + 2}
      aria-selected={selected}
      className={`${styles.row} ${index % 2 === 1 ? styles.rowAlt : ''} ${active ? styles.rowActive : ''} ${selected ? styles.rowSelected : ''} ${highlighted ? styles.rowHighlighted : ''}`}
      style={{ gridTemplateColumns: gridTemplate }}
      onClick={() => actions.activate(index)}
      onDoubleClick={() => actions.open(product)}
    >
      <div role="gridcell" className={styles.checkCell}>
        <input
          type="checkbox"
          tabIndex={-1}
          checked={selected}
          disabled={!canBulkOps}
          onChange={() => actions.toggleSelect(product.id)}
          onClick={(event) => event.stopPropagation()}
          aria-label={`Select ${name}`}
        />
      </div>
      {columns.map((column) => (
        <div
          key={column.id}
          role="gridcell"
          className={`${styles.cell} ${column.numeric ? styles.cellNumeric : ''} ${column.id === 'listed' || column.id === 'quantity' ? styles.cellEditableHost : ''}`}
        >
          {cell(column)}
        </div>
      ))}
    </div>
  )
})
